'use strict';

const libPictView = require('pict-view');

/**
 * SuperUserGrant — the #/GrantAccess screen. A bespoke, platform-super-user-only bootstrap tool: search ALL
 * users across every customer and grant a selected user the `Permission-Grant` capability (the right to grant
 * permissions), scoped to that user's own customer.
 *
 * This exists to break the chicken-and-egg: normal role/entitlement administration itself requires Permission-Grant,
 * so a customer with nobody holding it has no in-app way to bootstrap the first grantor. A level-5 super-user uses
 * this to seed that first person; from there the #/Entitlements and #/Access workspaces take over.
 *
 * The view is a thin shell (like Entitlements.js) — all logic + data + hand-built HTML live in the
 * SuperUserGrantProvider, which assigns into the #SUG-Body mount and writes through the RBAC grant path.
 */
class SuperUserGrant extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.SuperUserGrant; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			if (!tmpProvider.state.Loaded && !tmpProvider.state.Loading) { tmpProvider.loadAsync(); }
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = SuperUserGrant;
module.exports.default_configuration =
{
	ViewIdentifier: 'SuperUserGrant',
	DefaultRenderable: 'SuperUserGrantWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.sug-wrap { padding: 1.25rem 1.5rem 2rem; }
		.sug-head { margin: 0 0 1rem; }
		.sug-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.sug-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; max-width:60rem; }
		.sug-guard { border:1px solid var(--theme-color-status-warning,#b8860b); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.5rem; text-align:center; color:var(--theme-color-text-secondary,#45505f); }
		.sug-guard-title { font-size:1.05rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); margin:0 0 0.4rem; }
		.sug-filterbar { display:flex; gap:1rem; align-items:flex-end; flex-wrap:wrap; margin:0 0 1rem; }
		.sug-filter-field { display:flex; flex-direction:column; gap:0.3rem; }
		.sug-filter-label { font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#6b7686); }
		.sug-filter-customer { flex:0 0 auto; min-width:16rem; }
		.sug-filter-search { flex:1 1 22rem; }
		.sug-customer-picker { min-width:16rem; }
		.sug-search { width:100%; font-size:0.95rem; padding:0.55rem 0.75rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:8px; box-sizing:border-box; color:var(--theme-color-text-primary,#1f2733); background:var(--theme-color-background-input,#fff); }
		.sug-search:focus { outline:none; border-color:var(--theme-color-primary,#2f6fed); box-shadow:0 0 0 2px rgba(47,111,237,0.15); }
		.sug-cols { display:grid; grid-template-columns: 22rem 1fr; gap:1.1rem; align-items:start; }
		@media (max-width: 860px) { .sug-cols { grid-template-columns: 1fr; } }
		.sug-results { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.5rem; max-height:42rem; overflow:auto; }
		.sug-result { padding:0.5rem 0.6rem; border-radius:6px; cursor:pointer; border:1px solid transparent; }
		.sug-result:hover { background:var(--theme-color-background-secondary,#eef1f6); }
		.sug-result.is-active { background:var(--theme-color-brand-primary,#2e7d74); border-color:var(--theme-color-brand-primary,#2e7d74); }
		.sug-result.is-active .sug-result-name, .sug-result.is-active .sug-result-meta { color:#fff; }
		.sug-result-name { font-size:0.9rem; font-weight:600; color:var(--theme-color-text-primary,#1f2733); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.sug-result-meta { font-size:0.76rem; color:var(--theme-color-text-muted,#6b7686); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
		.sug-results-head { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#6b7686); padding:0.25rem 0.5rem 0.5rem; }
		.sug-result-tag { display:inline-block; margin-left:0.45rem; font-size:0.64rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; padding:0.05rem 0.4rem; border-radius:999px; background:var(--theme-color-background-tertiary,#eef1f6); color:var(--theme-color-text-secondary,#45505f); vertical-align:middle; }
		.sug-result.is-active .sug-result-tag { background:rgba(255,255,255,0.22); color:#fff; }
		.sug-result-tag-off { background:rgba(192,57,43,0.12) !important; color:#b23b2e !important; }
		.sug-link { color:var(--theme-color-primary,#2f6fed); cursor:pointer; text-decoration:underline; }
		.sug-hint, .sug-results-empty { font-size:0.82rem; color:var(--theme-color-text-muted,#8894a4); padding:0.6rem; }
		.sug-detail, .sug-detail-empty { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.1rem 1.25rem; min-height:20rem; }
		.sug-detail-empty { display:flex; align-items:center; justify-content:center; text-align:center; color:var(--theme-color-text-muted,#6b7686); }
		.sug-detail-name { font-size:1.2rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); }
		.sug-detail-sub { font-size:0.82rem; color:var(--theme-color-text-muted,#6b7686); margin-top:0.15rem; }
		.sug-facts { list-style:none; margin:0.9rem 0 1.1rem; padding:0; display:grid; grid-template-columns:auto 1fr; gap:0.3rem 1rem; font-size:0.86rem; }
		.sug-facts dt { color:var(--theme-color-text-muted,#6b7686); }
		.sug-facts dd { margin:0; color:var(--theme-color-text-primary,#1f2733); font-weight:600; }
		.sug-status { display:inline-flex; align-items:center; gap:0.4rem; font-size:0.85rem; font-weight:600; padding:0.25rem 0.6rem; border-radius:999px; }
		.sug-status-granted { color:#0b6b3a; background:rgba(16,150,80,0.12); }
		.sug-status-none { color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-tertiary,#eef1f6); }
		.sug-actions { margin-top:1.1rem; display:flex; align-items:center; gap:0.7rem; flex-wrap:wrap; }
		.sug-btn { font-size:0.86rem; font-weight:600; padding:0.5rem 1rem; border-radius:7px; cursor:pointer; border:1px solid transparent; }
		.sug-btn[disabled] { opacity:0.5; cursor:default; }
		.sug-btn-primary { color:#fff; background:var(--theme-color-primary,#2f6fed); }
		.sug-btn-ghost { color:var(--theme-color-text,#22303f); background:transparent; border-color:var(--theme-color-border-medium,#d5dbe3); }
		.sug-busy { font-size:0.84rem; color:var(--theme-color-primary,#2f6fed); font-weight:600; }
		.sug-note { font-size:0.84rem; color:var(--theme-color-text-muted,#6b7686); margin:0.9rem 0 0; }
		.sug-error { font-size:0.85rem; color:var(--theme-color-status-danger,#c0392b); margin:0.7rem 0 0; }
		.sug-ok { font-size:0.85rem; color:#0b6b3a; margin:0.7rem 0 0; }
		.sug-scope { font-size:0.82rem; color:var(--theme-color-text-secondary,#45505f); background:var(--theme-color-background-tertiary,#eef1f6); border-radius:7px; padding:0.55rem 0.7rem; margin:0.9rem 0 0; }
			.sug-cap { border:1px solid var(--theme-color-border-light,#e6eaf1); border-radius:8px; padding:0.85rem 1rem; margin:1rem 0 0; }
			.sug-cap-head { display:flex; align-items:center; justify-content:space-between; gap:0.6rem; flex-wrap:wrap; }
			.sug-cap-title { font-size:0.95rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); }
			.sug-cap .sug-scope { margin-top:0.55rem; }
			.sug-cap .sug-actions { margin-top:0.7rem; }
			.sug-cap code { font-size:0.85em; background:var(--theme-color-background-tertiary,#eef1f6); padding:0.05rem 0.3rem; border-radius:4px; color:var(--theme-color-text-primary,#1f2733); }
	`,
	Templates:
	[
		{
			Hash: 'SuperUserGrant-Wrap',
			Template: /*html*/`
				<div class="sug-wrap">
					<div class="sug-head">
						<h1 class="sug-title">Grant Access</h1>
						<p class="sug-subtitle">Bootstrap tool for platform super-users. Search any user across every customer and give them <strong>Permission&#8209;Grant</strong> — the right to grant permissions — scoped to their own customer. Use this to seed the first grantor for a customer; after that, the Entitlements and Access &amp; Permissions workspaces take over.</p>
					</div>
					<div id="SUG-Body"></div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'SuperUserGrantWrap',
			TemplateHash: 'SuperUserGrant-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
